/**
 * /settings → E-mail tab (admin-only). Beheert outbound-mail-config
 * voor de actieve workspace. Wachtwoord wordt versleuteld opgeslagen;
 * de UI ziet alleen `password_set: boolean` en gebruikt een placeholder
 * (`••••••••`) wanneer er al één is. "Verstuur test-mail" stuurt naar
 * de admin's eigen e-mailadres zodat hij/zij in 1 round-trip ziet of
 * de relay werkt.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type MailConfigPublic, type MailConfigPutBody } from "../api/client";
import { ld } from "./ledgerUi";

interface Props {
  /** De workspace, of `null` voor de globale instelling (super-admin): de
   *  terugval voor elke workspace en de mail voor de inlogcodes. */
  workspaceId: string | null;
}

/** Dezelfde vier aanroepen, voor een workspace of globaal. */
function mailApi(workspaceId: string | null): {
  get: () => Promise<{ config: MailConfigPublic | null }>;
  put: (body: MailConfigPutBody) => Promise<{ config: MailConfigPublic | null }>;
  remove: () => Promise<void>;
  test: () => Promise<{ ok: boolean; recipient: string }>;
} {
  return workspaceId
    ? {
        get: () => api.getMailConfig(workspaceId),
        put: (body) => api.putMailConfig(workspaceId, body),
        remove: () => api.deleteMailConfig(workspaceId),
        test: () => api.testMailConfig(workspaceId),
      }
    : {
        get: () => api.getGlobalMailConfig(),
        put: (body) => api.putGlobalMailConfig(body),
        remove: () => api.deleteGlobalMailConfig(),
        test: () => api.testGlobalMailConfig(),
      };
}

interface FormState {
  host: string;
  port: number;
  username: string;
  password: string;
  password_touched: boolean;
  from_address: string;
  from_name: string;
  transport: "mailchannels" | "smtp";
}

const EMPTY: FormState = {
  host: "smtp.gmail.com",
  port: 587,
  username: "",
  password: "",
  password_touched: false,
  from_address: "",
  from_name: "",
  transport: "mailchannels",
};

function formFromConfig(cfg: MailConfigPublic | null): FormState {
  if (!cfg) return { ...EMPTY };
  return {
    host: cfg.host,
    port: cfg.port,
    username: cfg.username,
    password: "",
    password_touched: false,
    from_address: cfg.from_address,
    from_name: cfg.from_name ?? "",
    transport: cfg.transport,
  };
}

export function MailConfigSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<MailConfigPublic | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await mailApi(workspaceId).get();
      setConfig(res.config);
      setForm(formFromConfig(res.config));
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Wachtwoord-veld semantiek:
      //  - leeg + niet aangeraakt → behoud huidige (null in body)
      //  - leeg + aangeraakt + er was er al één → wis (lege string)
      //  - leeg + aangeraakt + er was geen → null (laat 'm leeg)
      //  - waarde + aangeraakt → upload (string)
      let password: string | null;
      if (!form.password_touched) {
        password = null;
      } else if (form.password.length > 0) {
        password = form.password;
      } else {
        password = config?.password_set ? "" : null;
      }
      const res = await mailApi(workspaceId).put({
        host: form.host.trim(),
        port: form.port,
        username: form.username.trim(),
        password,
        from_address: form.from_address.trim(),
        from_name: form.from_name.trim() || null,
        transport: form.transport,
      });
      setConfig(res.config);
      setForm(formFromConfig(res.config));
      setInfo(t("settings.mail.savedToast"));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("settings.mail.removeConfirm"))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await mailApi(workspaceId).remove();
      setConfig(null);
      setForm(EMPTY);
      setInfo(t("settings.mail.removedToast"));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await mailApi(workspaceId).test();
      setInfo(t("settings.mail.testSuccess", { recipient: res.recipient }));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-5">
      <div className={`${ld.well} text-sm leading-6`}>
        <p className="mb-1">
          <strong>{t("settings.mail.introTitle")}</strong>
        </p>
        <ul className="ml-5 list-disc space-y-1 text-(--ld-muted)">
          <li>
            <strong>{t("settings.mail.mcLabel")}</strong>
            {t("settings.mail.mcDescriptionBefore")}
            <em>{t("settings.mail.mcWel")}</em>
            {t("settings.mail.mcDescriptionAfter")}
            <code>_mailchannels.&lt;domain&gt;</code>
            {t("settings.mail.mcDescriptionEnd")}
          </li>
          <li>
            <strong>{t("settings.mail.smtpLabel")}</strong>
            {t("settings.mail.smtpDescriptionBefore")}
            <em>{t("settings.mail.smtpHost")}</em>
            {t("settings.mail.smtpComma")}
            <em>{t("settings.mail.smtpPort")}</em>
            {t("settings.mail.smtpComma")}
            <em>{t("settings.mail.smtpUsername")}</em>
            {t("settings.mail.smtpEn")}
            <em>{t("settings.mail.smtpPassword")}</em>
            {t("settings.mail.smtpDescriptionAfter")}
          </li>
        </ul>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("settings.mail.fieldTransport")}>
          <select
            className={ld.input}
            value={form.transport}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                transport: e.target.value === "smtp" ? "smtp" : "mailchannels",
              }))
            }
          >
            <option value="mailchannels">{t("settings.mail.transportMailchannels")}</option>
            <option value="smtp">{t("settings.mail.transportSmtp")}</option>
          </select>
        </Field>
        <Field label={t("settings.mail.fieldFromAddress")}>
          <input
            type="email"
            className={ld.input}
            value={form.from_address}
            onChange={(e) => setForm((f) => ({ ...f, from_address: e.target.value }))}
            placeholder={t("settings.mail.fromAddressPlaceholder")}
            required
          />
        </Field>
        <Field label={t("settings.mail.fieldFromName")}>
          <input
            type="text"
            className={ld.input}
            value={form.from_name}
            onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
            placeholder={t("settings.mail.fromNamePlaceholder")}
          />
        </Field>
        <Field label={t("settings.mail.fieldSmtpHost")}>
          <input
            type="text"
            className={ld.input}
            value={form.host}
            onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
            placeholder={t("settings.mail.hostPlaceholder")}
          />
        </Field>
        <Field label={t("settings.mail.fieldPort")}>
          <input
            type="number"
            className={ld.input}
            value={form.port}
            onChange={(e) =>
              setForm((f) => ({ ...f, port: Number.parseInt(e.target.value, 10) || 0 }))
            }
            min={1}
            max={65535}
          />
        </Field>
        <Field label={t("settings.mail.fieldSmtpUsername")}>
          <input
            type="text"
            className={ld.input}
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            placeholder={t("settings.mail.usernamePlaceholder")}
            autoComplete="off"
          />
        </Field>
        <Field
          label={`${t("settings.mail.fieldSmtpPassword")}${
            config?.password_set && !form.password_touched ? t("settings.mail.passwordSet") : ""
          }`}
        >
          <input
            type="password"
            className={ld.input}
            value={form.password}
            onChange={(e) =>
              setForm((f) => ({ ...f, password: e.target.value, password_touched: true }))
            }
            placeholder={config?.password_set ? t("settings.mail.passwordPlaceholderSet") : ""}
            autoComplete="new-password"
          />
        </Field>
      </div>

      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}
      {info && (
        <p role="status" className={ld.success}>
          {info}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={ld.primary} onClick={() => void save()} disabled={busy}>
          {t("settings.mail.saveButton")}
        </button>
        <button
          type="button"
          className={ld.secondary}
          onClick={() => void test()}
          disabled={busy || !config}
          title={
            config
              ? t("settings.mail.testButtonTitleConfigured")
              : t("settings.mail.testButtonTitleNotConfigured")
          }
        >
          {t("settings.mail.testButton")}
        </button>
        {config && (
          <button
            type="button"
            className={`ml-auto ${ld.danger}`}
            onClick={() => void remove()}
            disabled={busy}
          >
            {t("settings.mail.clearConfig")}
          </button>
        )}
      </div>

      {config && (
        <p className="text-(--ld-muted) text-[13px]">
          {t("settings.mail.updatedBy", {
            user: config.updated_by,
            date: new Date(`${config.updated_at}Z`).toLocaleString(),
          })}
        </p>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // Het veld zit in het label, dus het label hoort er zonder id bij.
  return (
    <label className="block">
      <span className={ld.label}>{label}</span>
      {children}
    </label>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
